<script lang="ts">
  export enum SkeletonVariants {
    Text = 'text',
    Circular = 'circular',
    Rectangular = 'rectangular',
  }

  export type SkeletonVariant = `${SkeletonVariants}`;
</script>

<script setup lang="ts">
  import { computed, useCssModule, useSlots } from 'vue';

  export interface SkeletonProps {
    /** Pass a pixel value for circular and rectangular variants */
    height?: string | number;

    /** Either text, circular, or rectangular */
    variant?: SkeletonVariant;

    /** Pass a pixel value for any variant */
    width?: string | number;
  }

  const props = withDefaults(defineProps<SkeletonProps>(), {
    height: undefined,
    variant: SkeletonVariants.Text,
    width: undefined,
  });

  const slots = useSlots();
  const classes = useCssModule();

  const hasChildren = computed(() => !!slots['default']);

  const computedStyles = computed(() => {
    const styles: {
      width?: string;
      height?: string;
    } = {};

    if (props.width) {
      styles.width = `${props.width}px`;
    }

    // If the variant is text, let the font-size define the height
    if (props.height && props.variant !== SkeletonVariants.Text) {
      styles.height = `${props.height}px`;
    }

    return styles;
  });
</script>

<template>
  <div
    data-test="stash-skeleton"
    class="stash-skeleton animate-pulse bg-ice-500"
    :class="{
      'stash-skeleton--circular rounded-full': props.variant === SkeletonVariants.Circular,
      'stash-skeleton--text rounded': props.variant === SkeletonVariants.Text,
      [classes['text-variant']]: props.variant === SkeletonVariants.Text,
      'stash-skeleton--rectangular rounded': props.variant === SkeletonVariants.Rectangular,
      [classes['has-children']]: hasChildren,
      'max-w-fit': hasChildren && !props.width,
      'h-auto': hasChildren && !props.height,
    }"
    :style="computedStyles"
  >
    <!-- @slot Default slot for style inheritance -->
    <slot></slot>
  </div>
</template>

<style module>
  .text-variant::before {
    content: '\00a0';
  }

  .has-children > * {
    visibility: hidden;
  }
</style>
